import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getOrders, deleteOrder } from "../../redux/slices/orderSlice";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Pagination } from "../common/pagination/Pagination";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaEye, FaShoppingCart } from "react-icons/fa";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";
import type { OrderStatus } from "../../types/order/order.model";

type Props = {
  onDetail: (id: string) => void;
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  shipping: "Đang giao",
  completed: "Hoàn thành",
  cancelled: "Đã hủy",
};

const STATUS_TONE: Record<
  OrderStatus,
  "warning" | "info" | "neutral" | "success" | "danger"
> = {
  pending: "warning",
  confirmed: "info",
  shipping: "info",
  completed: "success",
  cancelled: "danger",
};

export const ListOrder = ({ onDetail }: Props) => {
  const dispatch = useAppDispatch();
  const { orders, pagination, loading } = useAppSelector(
    (state) => state.order,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const status = getParam("status", "all");
  const search = getParam("search", "");

  const handleChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parsedStatus = status === "all" ? undefined : (status as OrderStatus);

  useEffect(() => {
    dispatch(getOrders({ page, limit, status: parsedStatus, search }));
  }, [dispatch, page, limit, parsedStatus, search]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deleteOrder(confirmDeleteId)).unwrap();
      toast.success("Xóa đơn hàng thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("vi-VN", { style: "currency", currency: "VND" });

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý đơn hàng"
        subtitle={pagination ? `Tổng ${pagination.total} đơn hàng` : undefined}
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Mã đơn, tên khách hoặc SĐT..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <select
              name="status"
              value={status}
              onChange={handleChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="pending">Chờ xác nhận</option>
              <option value="confirmed">Đã xác nhận</option>
              <option value="shipping">Đang giao</option>
              <option value="completed">Hoàn thành</option>
              <option value="cancelled">Đã hủy</option>
            </select>
            <select
              name="limit"
              value={limit}
              onChange={handleChangeEvent}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        <DataTable
          data={orders}
          rowKey={(o) => o._id}
          loading={loading.list}
          minWidth="900px"
          columns={[
            {
              key: "code",
              header: "Mã đơn",
              align: "left",
              render: (o) => (
                <span className="font-mono font-semibold text-blue-600">
                  {o.orderCode}
                </span>
              ),
            },
            {
              key: "customer",
              header: "Khách hàng",
              align: "left",
              render: (o) => (
                <div className="leading-tight">
                  <p className="font-medium text-gray-900">
                    {o.customer.name}
                  </p>
                  <p className="text-xs text-gray-500">{o.customer.phone}</p>
                </div>
              ),
            },
            {
              key: "total",
              header: "Tổng tiền",
              align: "right",
              render: (o) => (
                <span className="font-semibold text-gray-900">
                  {formatCurrency(o.totalAmount)}
                </span>
              ),
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (o) => (
                <StatusBadge
                  tone={STATUS_TONE[o.status]}
                  label={STATUS_LABELS[o.status]}
                />
              ),
            },
            {
              key: "created",
              header: "Ngày đặt",
              align: "center",
              width: "140px",
              render: (o) => (
                <span className="text-xs">{formatDate(o.createdAt)}</span>
              ),
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (o) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Xem chi tiết"
                    onClick={() => onDetail(o._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <FaEye />
                  </button>
                  {(o.status === "cancelled" || o.status === "completed") && (
                    <button
                      type="button"
                      title="Xóa đơn"
                      onClick={() => setConfirmDeleteId(o._id)}
                      className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <RiDeleteBinLine className="text-lg" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaShoppingCart />}
              title={
                search || status !== "all"
                  ? "Không tìm thấy đơn hàng nào"
                  : "Chưa có đơn hàng nào"
              }
              description={
                search || status !== "all"
                  ? "Thử bỏ bớt bộ lọc hoặc tìm với từ khóa khác"
                  : "Khi có khách đặt hàng, đơn sẽ hiển thị tại đây"
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa đơn hàng này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
